<template>
    <div>
        <template v-if="!newVersion">
            <md-field :class="['md-datepicker', { 'md-native': !this.mdOverrideNative }]" :md-clearable="mdClearable" @md-clear="onClear">
                <md-icon class="md-icon-image md-date-icon" v-once @click.native="toggleDialog">
                    <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" />
                        <path d="M0 0h24v24H0z" fill="none" />
                    </svg>
                </md-icon>
                <md-input :type="type" ref="input" v-model="inputDate" @focus.native="onFocus" :pattern="pattern" />
                
                <slot />
                
                <keep-alive>
                    <md-datepicker-dialog
                        v-if="showDialog"
                        :md-date.sync="localDate"
                        :md-disabled-dates="mdDisabledDates"
                        :md-immediately="mdImmediately"
                        @md-closed="toggleDialog"
                    />
                </keep-alive>
                
                <md-overlay class="md-datepicker-overlay" md-fixed :md-active="showDialog" @click="toggleDialog" />
            </md-field>
        </template>
        <template v-else>
            <formInput
                @click.native="toggleDialog"
                :label="label"
                :input-name="'date_of_birth'"
                :input-type="type"
                :placeholder="label || dateFormat"
                v-model="inputDate"
                :dusk="'date-of-birth-input'"
                :submitting="false"
                :has-error="!!error"
                :error="error"
                :new-version="newVersion"
                prepend-icon="calendar"
            />
            <keep-alive>
                <md-datepicker-dialog
                    v-if="showDialog"
                    :md-date.sync="localDate"
                    :md-disabled-dates="mdDisabledDates"
                    :md-immediately="mdImmediately"
                    @md-closed="toggleDialog"
                />
            </keep-alive>
            
            <md-overlay class="md-datepicker-overlay" md-fixed :md-active="showDialog" @click="toggleDialog" />
        </template>
    </div>
</template>

<script>
import Vue from 'vue';
import isFirefox from 'is-firefox';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import isValid from 'date-fns/isValid';
import MdDatepickerDialog from './MdDatepickerDialog';
import debounce from 'lodash/debounce';
import MdOverlay from 'vue-material';
import formInput from '../../registration/Profile/formInput.vue';

export default {
    name: 'MdDatepicker',
    components: {
        MdDatepickerDialog,
        MdOverlay,
        formInput
    },
    props: {
        value: [String, Number, Date],
        mdDisabledDates: [Array, Function],
        mdOpenOnFocus: {
            type: Boolean,
            default: true
        },
        mdOverrideNative: {
            type: Boolean,
            default: true
        },
        mdImmediately: {
            type: Boolean,
            default: false
        },
        mdModelType: {
            type: Function,
            default: Date,
        },
        mdDebounce: {
            type: Number,
            default: 1000
        },
        mdClearable: {
            type: Boolean,
            default: true
        },
        newVersion: {
            type: Boolean,
            default: false
        },
        label: {
            type: String,
            default: null
        },
        error: {
            default: null
        }
    },
    data: () => ({
        showDialog: false,
        // String for input
        inputDate: '',
        // Date for real value
        localDate: null
    }),
    computed: {
        locale () {
            return this.$material.locale;
        },
        type () {
            return this.mdOverrideNative
                ? 'text'
                : 'date';
        },
        dateFormat () {
            return this.$t('global.vue-material-date-format');
        },
        modelType () {
            if (this.isModelTypeString) {
                return String;
            } else if (this.isModelTypeNumber) {
                return Number;
            } else if (this.isModelTypeDate) {
                return Date;
            } else {
                return this.mdModelType;
            }
        },
        isModelNull () {
            return this.value === null || this.value === undefined;
        },
        isModelTypeString () {
            return typeof this.value === 'string';
        },
        isModelTypeNumber () {
            return Number.isInteger(this.value) && this.value >= 0;
        },
        isModelTypeDate () {
            return typeof this.value === 'object' && this.value instanceof Date && isValid(this.value);
        },
        localString () {
            return this.localDate && format(this.localDate, this.dateFormat);
        },
        localNumber () {
            return this.localDate && Number(this.localDate);
        },
        parsedInputDate () {
            const parsedDate = parse(this.inputDate, this.dateFormat, new Date());
            return parsedDate && isValid(parsedDate) ? parsedDate : null;
        },
        pattern () {
            return this.dateFormat.replace(/yyyy|MM|dd/g, match => {
                switch (match) {
                case 'yyyy':
                    return '[0-9]{4}';
                case 'MM':
                case 'dd':
                    return '[0-9]{2}';
                }
            });
        }
    },
    watch: {
        inputDate (value) {
            this.inputDateToLocalDate();
        },
        localDate () {
            this.inputDate = this.localString;
            if (this.modelType === Date) {
                this.$emit('input', this.localDate);
            }
        },
        localString () {
            if (this.modelType === String) {
                this.$emit('input', this.localString);
            }
        },
        localNumber () {
            if (this.modelType === Number) {
                this.$emit('input', this.localNumber);
            }
        },
        value: {
            immediate: true,
            handler () {
                this.valueDateToLocalDate();
            }
        },
        mdModelType (type) {
            switch (type) {
            case Date:
                this.$emit('input', this.localDate);
                break;
            case String:
                this.$emit('input', this.localString);
                break;
            case Number:
                this.$emit('input', this.localNumber);
                break;
            }
        },
        dateFormat () {
            if (this.localDate) {
                this.inputDate = format(this.localDate, this.dateFormat);
            }
        }
    },
    created () {
        this.inputDateToLocalDate = debounce(this.inputDateToLocalDate, this.MdDebounce);
    },
    methods: {
        toggleDialog () {
            if (!isFirefox || this.mdOverrideNative) {
                this.showDialog = !this.showDialog;
                if (this.showDialog) {
                    this.$emit('md-opened');
                } else {
                    this.$emit('md-closed');
                }
            } else {
                this.$refs.input.$el.click();
            }
        },
        onFocus () {
            if (this.mdOpenOnFocus) {
                this.toggleDialog();
            }
        },
        inputDateToLocalDate () {
            if (this.inputDate) {
                if (this.parsedInputDate) {
                    this.localDate = this.parsedInputDate;
                }
            } else {
                this.localDate = null;
            }
        },
        valueDateToLocalDate () {
            if (this.isModelNull) {
                this.localDate = null;
            } else if (this.isModelTypeNumber) {
                this.localDate = new Date(this.value);
            } else if (this.isModelTypeDate) {
                this.localDate = this.value;
            } else if (this.isModelTypeString) {
                let parsedDate = parse(this.value, this.dateFormat, new Date());
                
                if (isValid(parsedDate)) {
                    this.localDate = parse(this.value, this.dateFormat, new Date());
                } else {
                    Vue.util.warn(`The datepicker value is not a valid date. Given value: ${this.value}, format: ${this.dateFormat}`);
                }
            } else {
                Vue.util.warn(`The datepicker value is not a valid date. Given value: ${this.value}`);
            }
        },
        onClear () {
            this.$emit('md-clear');
        }
    }
};
</script>

<style lang="scss">
@import "../MdAnimation/variables";
@import "../MdLayout/mixins";

.md-datepicker-overlay {
    opacity: 0;
    
    @include md-layout-xsmall {
        opacity: 1;
    }
}

.md-datepicker {
    &.md-native {
        label {
            top: 0 !important;
        }
    }
    
    .md-date-icon {
        cursor: pointer;
    }
    
    input[type=date]::-webkit-clear-button,
    input[type=date]::-webkit-inner-spin-button,
    input[type=date]::-webkit-calendar-picker-indicator {
        display: none;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/vue-material-overrides/MdDatepicker/MdDatepicker.vue